import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate }                 from "react-router-dom";
import { ContentHeader }                          from "@components";
import { useAuth }                                from "../contexts/AuthContext";
import API                                        from "../api";
import toastr                                     from "toastr";

interface TestResult {
  item_id: number;
  test_name: string;
  test_code: string;
  remarks: string;         
  test_category: string;
  type_test: string;
  units: string;
  lower_limit: string;
  upper_limit: string;
  sub_head: string;
  table_input: string;    
  test_result?: string;
  paragraph?: string;
}

interface BillData {
  id: number;
  bill_no: string;
  test_status: number;     
  patient_id: number;
  entry_date: string;
  entry_time: string;
  net_amount: number;
  advance_amount: number;
  ref_prefix: string;
  reference: string;
  modified: string;
  release_date?: string;
  release_time?: string;
}

interface PatientInfo {
  id: number;
  P_name: string;
  prefix: string;
  gender: string;
  age: string;
  age_type: string;
}

const isNumberDecimalKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
  const charCode = e.which ?? e.keyCode;
  if (charCode !== 46 && charCode > 31 && (charCode < 48 || charCode > 57)) {
    e.preventDefault();
  }
};

const AddTestEntry: React.FC = () => {
  const { id }    = useParams<{ id: string }>();       
  const entryId   = Number(id);
  const navigate  = useNavigate();
  const { user: authUser } = useAuth();
  const canSetReleaseDateTime = [1, 2].includes(Number(authUser?.user_type_id));

  const [testResults, setTestResults]   = useState<TestResult[]>([]);
  const [billData, setBillData]         = useState<BillData | null>(null);
  const [patientInfo, setPatientInfo]   = useState<PatientInfo | null>(null);
  const [saving, setSaving]             = useState(false);
  const [values, setValues]             = useState<Record<number, Record<string, string>>>({});
  const [releaseDate, setReleaseDate]   = useState<string>("");
  const [releaseTime, setReleaseTime]   = useState<string>("");

  useEffect(() => {
    (async () => {
      try {
        const res = await API.get(`/report/add_test_entry/${entryId}`);
        const { billData, patient, testResults: tests } = res.data;

        setBillData(billData);
        setPatientInfo(patient);
        setTestResults(tests);

        if (billData?.release_date) {
          setReleaseDate(billData.release_date);
        } else if (billData?.modified) {
          const m = String(billData.modified).match(/^(\d{4}-\d{2}-\d{2})/);
          if (m) setReleaseDate(m[1]);
        }

        if (billData?.release_time) {
          setReleaseTime(billData.release_time);
        } else if (billData?.modified) {
          const t = String(billData.modified).match(/[T\s](\d{2}:\d{2})/);
          if (t) setReleaseTime(t[1]);
        }

        const initial: Record<number, Record<string, string>> = {};
        tests.forEach((t: TestResult) => {
          initial[t.item_id] = {
            test_result: t.test_result ?? "",
            paragraph:   t.paragraph   ?? "",
            sub_head:    "",
            date:        "",
            time:        "",
          };
        });
        setValues(initial);
      } catch {
        toastr.error("Failed to load test entry data");
      }
    })();
  }, [entryId]);

  const handleChange = (testId: number, field: string, val: string) => {
    setValues(prev => ({
      ...prev,
      [testId]: { ...prev[testId], [field]: val },
    }));
  };

  const calculate = useCallback(() => {
    const updatedValues = { ...values };

    testResults.forEach(t => {
      if (!t.remarks) return;
      let formula = t.remarks;

      testResults.forEach(other => {
        const val = updatedValues[other.item_id]?.test_result ?? "";
        formula = formula.replace(new RegExp(other.test_code, "g"), val || "0");
      });

      try {
        const result = parseFloat(eval(formula).toFixed(2));
        const str    = result.toString().replace(".00", "");
        updatedValues[t.item_id] = { ...updatedValues[t.item_id], test_result: str };
      } catch {
      }
    });

    setValues(updatedValues);
  }, [testResults, values]);

  const buildPayload = (testStatus: number) => {
    const testData = testResults.map(t => {
      const v = values[t.item_id] ?? {};
      return [
        t.item_id,
        v.test_result ?? "",
        v.sub_head    ?? "",
        v.paragraph   ?? "",
        "",            
        "", "", "", "", "",
        "",           
        "", "", "", "", "",
        v.date ?? "",
        v.time ?? "",
      ].join(",");
    });

    return {
      entry_id: entryId,
      test_status: testStatus,
      test_data: JSON.stringify(testData),
      release_date: releaseDate || null,
      release_time: releaseTime || null,
    };
  };

  const submitData = async () => {
    setSaving(true);
    try {
      await API.post("/report/test_entry_save", buildPayload(2));
      toastr.success("Test Details Added Successfully!");
      window.location.reload();
    } catch {
      toastr.error("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const completeTest = async () => {
    setSaving(true);
    try {
      await API.post("/report/test_entry_save", buildPayload(1));
      toastr.success("Test Entry Completed Successfully!");
      navigate("/patients-reports");
    } catch {
      toastr.error("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  /* ---------------------------------------------------------------- */
  /*  Track category / sub-heading for grouping                        */
  /* ---------------------------------------------------------------- */
  let lastCategory = "";
  let lastSubHead  = "";

  /* ---------------------------------------------------------------- */
  /*  Render                                                           */
  /* ---------------------------------------------------------------- */
  return (
    <div>
      <div className="d-flex justify-content-between align-items-center">
        <ContentHeader title="Add Test Entry" />
      </div>

      <section className="content">
        <div className="container-fluid">
          {/* Patient summary */}
          {billData && patientInfo && (
            <div className="card mb-3">
              <div className="card-body py-2">
                <div className="row">
                  <div className="col">
                    <strong>Patient:</strong>{" "}
                    {patientInfo.prefix}.{patientInfo.P_name}
                  </div>
                  <div className="col">
                    <strong>Age/Sex:</strong>{" "}
                    {patientInfo.age}{patientInfo.age_type} {patientInfo.gender}
                  </div>
                  <div className="col">
                    <strong>Bill No:</strong> {billData.bill_no}
                  </div>
                  <div className="col">
                    <strong>Date:</strong>{" "}
                    {new Date(billData.entry_date).toLocaleDateString("en-IN")}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Release Date & Time for auth user_type_id 1, 2 */}
          {canSetReleaseDateTime && (
            <div className="card mb-3 border-primary-subtle shadow-sm">
              <div className="card-body py-2">
                <div className="row align-items-center">
                  <div className="col-auto">
                    <strong className="text-primary">
                      <i className="fa fa-calendar-alt me-1" /> Report Release Date & Time:
                    </strong>
                    <small className="text-muted ms-1">(Optional)</small>
                  </div>
                  <div className="col-md-3 col-sm-6 my-1">
                    <div className="input-group input-group-sm">
                      <span className="input-group-text bg-light">
                        <i className="fa fa-calendar" />
                      </span>
                      <input
                        type="date"
                        className="form-control form-control-sm"
                        value={releaseDate}
                        onChange={(e) => setReleaseDate(e.target.value)}
                        placeholder="YYYY-MM-DD"
                      />
                    </div>
                  </div>
                  <div className="col-md-3 col-sm-6 my-1">
                    <div className="input-group input-group-sm">
                      <span className="input-group-text bg-light">
                        <i className="fa fa-clock" />
                      </span>
                      <input
                        type="time"
                        className="form-control form-control-sm"
                        value={releaseTime}
                        onChange={(e) => setReleaseTime(e.target.value)}
                        placeholder="HH:MM"
                      />
                    </div>
                  </div>
                  {(releaseDate || releaseTime) && (
                    <div className="col-auto my-1">
                      <button
                        type="button"
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() => {
                          setReleaseDate("");
                          setReleaseTime("");
                        }}
                        title="Clear custom release date & time"
                      >
                        <i className="fa fa-times me-1" /> Clear
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="card">
            <div className="card-body">
              <div className="table-responsive">
                <table className="table table-striped table-bordered">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }}>#</th>
                      <th>Test Name</th>
                      <th colSpan={3}>Result</th>
                      <th>Unit</th>
                      <th>Lower Limit</th>
                      <th>Upper Limit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {testResults.map((t, idx) => {
                      const showCategory = t.test_category !== lastCategory;
                      const showSubHead  =
                        t.sub_head !== lastSubHead && t.sub_head?.trim() !== "";

                      if (showCategory) lastCategory = t.test_category;
                      if (showSubHead)  lastSubHead  = t.sub_head;

                      const v = values[t.item_id] ?? {};

                      return (
                        <React.Fragment key={t.item_id}>
                          {/* Category heading */}
                          {showCategory && (
                            <tr>
                              <td
                                colSpan={8}
                                className="text-center fw-bold bg-light"
                              >
                                {t.test_name}
                              </td>
                            </tr>
                          )}

                          {/* Sub-heading */}
                          {showSubHead && (
                            <tr>
                              <td
                                colSpan={8}
                                style={{
                                  textDecoration: "underline",
                                  fontWeight: "bold",
                                }}
                              >
                                {t.sub_head}
                              </td>
                            </tr>
                          )}

                          {/* ---- Normal ---- */}
                          {t.type_test === "Normal" && (
                            <tr>
                              <td>{idx + 1}</td>
                              <td>
                                {t.test_name}
                                {t.remarks?.trim() && (
                                  <span style={{ color: "red" }}>*</span>
                                )}
                              </td>
                              <td colSpan={3}>
                                <input
                                  className="form-control form-control-sm"
                                  type="text"
                                  value={v.test_result ?? ""}
                                  onChange={e =>
                                    handleChange(t.item_id, "test_result", e.target.value)
                                  }
                                />
                              </td>
                              <td>{t.units}</td>
                              <td>{t.lower_limit}</td>
                              <td>{t.upper_limit}</td>
                            </tr>
                          )}

                          {/* ---- Sub Heading ---- */}
                          {t.type_test === "Sub Heading" && (
                            <tr>
                              <td>{idx + 1}</td>
                              <td>{t.test_name}</td>
                              <td colSpan={6}>
                                <input
                                  className="form-control form-control-sm"
                                  type="text"
                                  value={v.sub_head ?? ""}
                                  onChange={e =>
                                    handleChange(t.item_id, "sub_head", e.target.value)
                                  }
                                />
                              </td>
                            </tr>
                          )}

                          {/* ---- Paragraph ---- */}
                          {t.type_test === "Paragraph" && (
                            <tr>
                              <td>{idx + 1}</td>
                              <td>{t.test_name}</td>
                              <td colSpan={6}>
                                <textarea
                                  className="form-control"
                                  rows={3}
                                  value={v.paragraph ?? ""}
                                  onChange={e =>
                                    handleChange(t.item_id, "paragraph", e.target.value)
                                  }
                                />
                              </td>
                            </tr>
                          )}

                          {/* ---- Table ---- */}
                          {t.type_test === "Table" && (
                            <tr>
                              <td>{idx + 1}</td>
                              <td>{t.test_name}</td>
                              <td colSpan={3}>
                                <input
                                  className="form-control form-control-sm"
                                  type="text"
                                  value={v.test_result ?? ""}
                                  onChange={e =>
                                    handleChange(t.item_id, "test_result", e.target.value)
                                  }
                                />
                              </td>
                              <td>{t.units}</td>
                              <td colSpan={2}>
                                <select
                                  className="form-select form-select-sm"
                                  value={v.test_result ?? ""}
                                  onChange={e =>
                                    handleChange(t.item_id, "test_result", e.target.value)
                                  }
                                >
                                  <option value="">Result Value</option>
                                  {(t.table_input ?? "")
                                    .split(",")
                                    .filter(Boolean)
                                    .map(opt => (
                                      <option key={opt} value={opt}>
                                        {opt}
                                      </option>
                                    ))}
                                </select>
                              </td>
                            </tr>
                          )}

                          {/* ---- Date ---- */}
                          {t.type_test === "Date" && (
                            <tr>
                              <td>{idx + 1}</td>
                              <td>{t.test_name}</td>
                              <td colSpan={6}>
                                <input
                                  className="form-control form-control-sm"
                                  type="date"
                                  value={v.date ?? ""}
                                  onChange={e =>
                                    handleChange(t.item_id, "date", e.target.value)
                                  }
                                />
                              </td>
                            </tr>
                          )}

                          {/* ---- Time ---- */}
                          {t.type_test === "Time" && (
                            <tr>
                              <td>{idx + 1}</td>
                              <td>{t.test_name}</td>
                              <td colSpan={6}>
                                <input
                                  className="form-control form-control-sm"
                                  type="time"
                                  value={v.time ?? ""}
                                  onChange={e =>
                                    handleChange(t.item_id, "time", e.target.value)
                                  }
                                />
                              </td>
                            </tr>
                          )}

                          {/* ---- Image / File Upload ---- */}
                          {t.type_test === "Image" && (
                            <tr>
                              <td>{idx + 1}</td>
                              <td>{t.test_name}</td>
                              <td colSpan={6}>
                                <button
                                  type="button"
                                  className="btn btn-light btn-sm"
                                  title="Document Upload"
                                  onClick={() =>
                                    toastr.info("File upload feature – open upload modal")
                                  }
                                >
                                  <i className="fa fa-upload me-1" /> File Upload
                                </button>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* ---- Action buttons ---- */}
              <div className="d-flex gap-2 justify-content-end mt-3">
                <button
                  type="button"
                  className="btn btn-light"
                  onClick={calculate}
                  disabled={saving}
                >
                  Calculate
                </button>

                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => navigate(-1)}
                >
                  Back to List
                </button>

                <button
                  type="button"
                  className="btn btn-light"
                  onClick={submitData}
                  disabled={saving}
                >
                  {saving ? "Saving…" : "Save Data"}
                </button>

                {/* Preview & Complete shown only when test_status === 2 (saved) */}
                {billData?.test_status === 2 && (
                  <>
                    <button
                      type="button"
                      className="btn btn-light"
                      onClick={() => navigate(`/report/test_receipt/${entryId}?header=0`)}
                    >
                      Preview
                    </button>
                    <button
                      type="button"
                      className="btn btn-light"
                      onClick={completeTest}
                      disabled={saving}
                    >
                      Complete Test
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AddTestEntry;